import { useState } from 'react';
import { motion } from 'motion/react';
import { ImageWithFallback } from './components/figma/ImageWithFallback';
import PreRegisterForm from './components/PreRegisterForm';
import { CompassIcon, TicketIcon, MapIcon, PercentIcon, StarIcon, ShieldIcon, ClockIcon } from './components/Icons';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { Badge } from './components/ui/badge';
import { Card, CardContent } from './components/ui/card';

// Nuevas imágenes de Unsplash
const mexicoDestinationImage = "https://images.unsplash.com/photo-1549727636-36589a0cf6f5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtZXhpY28lMjB0cmF2ZWwlMjBkZXN0aW5hdGlvbnxlbnwxfHx8fDE3NTU3NTQ2NjZ8MA&ixlib=rb-4.1.0&q=80&w=1080";
const phoneMockupImage = "https://images.unsplash.com/photo-1641567535859-c58187ac4954?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaG9uZSUyMG1vY2t1cCUyMGludGVyZmFjZSUyMGRlc2lnbnxlbnwxfHx8fDE3NTU3NTQ2NzB8MA&ixlib=rb-4.1.0&q=80&w=1080";
const coupleTravelersImage = "https://images.unsplash.com/photo-1724637436155-5353f859007a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3VwbGUlMjB0cmF2ZWxlcnMlMjBoYXBweSUyMHZhY2F0aW9ufGVufDF8fHx8MTc1NTc1NDY3NHww&ixlib=rb-4.1.0&q=80&w=1080";

export default function App() {
  const [showSuccess, setShowSuccess] = useState(false);

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#3234DA' }}>
        <motion.div 
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
              className="text-6xl mb-6"
            >
              ✈️
            </motion.div>
            <h1 className="text-3xl sm:text-4xl text-gray-800 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              ¡Tu aventura está por comenzar!
            </h1>
            <p className="text-lg text-gray-600 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Gracias por unirte a la revolución del turismo digital en México. Te notificaremos cuando Bitravel esté listo.
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
              <h3 className="text-gray-800 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                ¿Qué viene después?
              </h3>
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white">
                    🚀
                  </div>
                  <p style={{ fontFamily: 'Poppins, sans-serif' }}>Beta exclusiva</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white">
                    💳
                  </div>
                  <p style={{ fontFamily: 'Poppins, sans-serif' }}>Descuentos VIP</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2 text-white">
                    📱
                  </div>
                  <p style={{ fontFamily: 'Poppins, sans-serif' }}>App completa</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
      {/* Header with Bitravel Blue Background */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: '#3234DA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <img 
              src="/bitravel-logo-white.svg?v=5" 
              alt="Bitravel Logo" 
              className="h-12 w-auto"
            />
            <div className="flex items-center gap-4">
              <Badge 
                className="px-4 py-2 rounded-full text-sm border border-white/20"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
              >
                🚀 Beta próximamente
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Split Layout */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#3234DA' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
            backgroundSize: '100px 100px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Hero Content - 7 columns */}
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div 
                className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span className="text-white text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  🇲🇽 Diseñado para México
                </span>
              </motion.div>

              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Descubre México con{' '}
                <span className="relative">
                  <span style={{ color: '#F9C641' }}>inteligencia artificial</span>
                  <motion.div 
                    className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                    style={{ backgroundColor: '#F9C641' }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  />
                </span>
              </h1>
              
              <p 
                className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed max-w-xl"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                La primera app que combina IA, mapas offline y experiencias locales para hacer tu viaje por México más fácil, seguro e inolvidable.
              </p>
              
              {/* CTA Form */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <PreRegisterForm 
                  onSuccess={() => setShowSuccess(true)}
                  className="max-w-md"
                  buttonColor="#F9C641"
                  buttonText="Únete a la beta gratuita"
                />
              </motion.div>
              
              {/* Trust Indicators */}
              <motion.div 
                className="flex items-center gap-6 mt-8 text-sm text-white/80"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="flex items-center gap-2">
                  <StarIcon className="w-4 h-4" style={{ color: '#F9C641' }} />
                  <span>100% gratuito</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldIcon className="w-4 h-4" style={{ color: '#4CAF50' }} />
                  <span>Datos seguros</span>
                </div>
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4" style={{ color: '#FF7043' }} />
                  <span>Lanzamiento 2025</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Visual - 5 columns */}
            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative">
                {/* Main Image */}
                <div className="relative z-10">
                  <ImageWithFallback
                    src={mexicoDestinationImage}
                    alt="Destino turístico en México"
                    className="rounded-3xl shadow-2xl w-full h-80 sm:h-96 object-cover"
                  />
                  
                  {/* Floating Stats Card */}
                  <motion.div 
                    className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-4 max-w-xs"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <ImageWithFallback
                          src={phoneMockupImage}
                          alt="App interface"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          Itinerario IA generado
                        </p>
                        <p className="text-xs text-gray-600">Riviera Maya • 5 días</p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-1">
                      {[1,2,3,4,5].map((star) => (
                        <StarIcon key={star} className="w-3 h-3 fill-current" style={{ color: '#F9C641' }} />
                      ))}
                    </div>
                  </motion.div>
                  
                  {/* Floating Feature Badge */}
                  <motion.div 
                    className="absolute -top-4 -right-4 bg-gradient-to-r rounded-2xl shadow-lg p-3"
                    style={{ background: 'linear-gradient(135deg, #FF7043, #F9C641)' }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 1 }}
                  >
                    <div className="text-white text-center">
                      <p className="text-lg font-bold">+500</p>
                      <p className="text-xs">Experiencias</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Poppins, sans-serif', color: '#263238' }}>
              Todo lo que necesitas en <span style={{ color: '#3234DA' }}>una sola app</span>
            </h2>
            <p className="text-lg max-w-3xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
              Bitravel combina la potencia de la inteligencia artificial con el conocimiento local para ofrecerte la mejor experiencia de viaje posible.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: CompassIcon,
                title: "IA Personalizada",
                description: "Itinerarios únicos creados específicamente para tus gustos, presupuesto y tiempo disponible",
                color: "#3234DA"
              },
              {
                icon: MapIcon,
                title: "Mapas sin Internet",
                description: "Navega sin conexión con mapas detallados y puntos de interés actualizados",
                color: "#4CAF50"
              },
              {
                icon: TicketIcon,
                title: "Reservas Seguras",
                description: "Booking integrado con garantía total en tours, hoteles y experiencias verificadas",
                color: "#FF7043"
              },
              {
                icon: PercentIcon,
                title: "Descuentos VIP",
                description: "Acceso a ofertas exclusivas en restaurantes, tiendas y actividades locales",
                color: "#F9C641"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 border-0 shadow-md">
                  <CardContent className="p-6 text-center">
                    <div 
                      className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <feature.icon className="w-8 h-8" style={{ color: feature.color }} />
                    </div>
                    <h3 className="text-lg mb-3" style={{ fontFamily: 'Poppins, sans-serif', color: '#263238' }}>
                      {feature.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 sm:py-24" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Badge 
                className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full"
                style={{ backgroundColor: '#3234DA20', color: '#3234DA' }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#3234DA' }}></span>
                ¿Qué es Bitravel?
              </Badge>
              
              <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Poppins, sans-serif', color: '#263238' }}>
                Tu compañero inteligente de viajes por México
              </h2>
              
              <div className="space-y-4 mb-8">
                <p className="text-lg" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                  Bitravel es la primera plataforma de turismo en México que utiliza inteligencia artificial para crear experiencias de viaje completamente personalizadas.
                </p>
                
                <p style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                  Desde la planificación hasta el regreso a casa, te acompañamos en cada paso con recomendaciones inteligentes, mapas offline, traducción automática y acceso a experiencias únicas que solo los locales conocen.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#4CAF5020' }}>
                    <span style={{ color: '#4CAF50' }}>✓</span>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                    100% enfocado en México
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#4CAF5020' }}>
                    <span style={{ color: '#4CAF50' }}>✓</span>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                    Powered by AI
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#4CAF5020' }}>
                    <span style={{ color: '#4CAF50' }}>✓</span>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                    Gratuito para viajeros
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#4CAF5020' }}>
                    <span style={{ color: '#4CAF50' }}>✓</span>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                    Experiencias verificadas
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <ImageWithFallback
                  src={coupleTravelersImage}
                  alt="Pareja de viajeros felices"
                  className="rounded-3xl shadow-xl w-full h-96 object-cover"
                />
                
                {/* Testimonial overlay */}
                <motion.div 
                  className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {[1,2,3,4,5].map((star) => (
                      <StarIcon key={star} className="w-4 h-4 fill-current" style={{ color: '#F9C641' }} />
                    ))}
                  </div>
                  <p className="text-sm italic mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: '#263238' }}>
                    "Bitravel nos ayudó a descubrir lugares increíbles que jamás habríamos encontrado solos."
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                    Ana & Carlos • Yucatán 2024
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Poppins, sans-serif', color: '#263238' }}>
              Preguntas frecuentes
            </h2>
            <p className="text-lg" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
              Todo lo que necesitas saber sobre Bitravel
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {[
                {
                  question: "¿Bitravel es realmente gratuito?",
                  answer: "Sí, completamente gratuito para viajeros. Generamos ingresos a través de comisiones con proveedores de servicios, pero tú nunca pagas nada extra."
                },
                {
                  question: "¿Cómo funciona la inteligencia artificial?",
                  answer: "Nuestra IA analiza tus preferencias, presupuesto, tiempo disponible y miles de datos sobre destinos mexicanos para crear itinerarios únicos y personalizados en tiempo real."
                },
                {
                  question: "¿Puedo usar la app sin internet?",
                  answer: "Sí, los mapas y la información esencial están disponibles offline. Solo necesitas conexión para hacer reservas y obtener actualizaciones en tiempo real."
                }
              ].map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border rounded-2xl px-6">
                  <AccordionTrigger className="text-left" style={{ fontFamily: 'Poppins, sans-serif', color: '#263238' }}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24" style={{ backgroundColor: '#3234DA' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Únete a la revolución del turismo en México
            </h2>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Sé de los primeros en experimentar cómo la inteligencia artificial transformará la forma en que exploras México.
            </p>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 max-w-lg mx-auto">
              <PreRegisterForm 
                onSuccess={() => setShowSuccess(true)}
                buttonText="Acceso anticipado gratis"
                buttonColor="#F9C641"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="flex items-center gap-4">
              <img 
                src="/bitravel-logo-blue.svg?v=5" 
                alt="Bitravel Logo" 
                className="h-10 w-auto"
              />
              <div>
                <p className="text-sm" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                  Transformando el turismo mexicano con IA
                </p>
                <p className="text-xs" style={{ fontFamily: 'Poppins, sans-serif', color: '#607D8B' }}>
                  © 2025 Bitravel. Todos los derechos reservados.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Badge 
                className="px-4 py-2 rounded-full"
                style={{ backgroundColor: '#3234DA20', color: '#3234DA' }}
              >
                🇲🇽 Hecho con amor en México
              </Badge>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
